import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import React from "react";
import type { Point } from "src/components/DocumentViewer/V2/types";
import { PersonProfileLink } from "src/components/Links/PersonProfileLink";
import { formatIsoDate } from "src/services/format";
import * as GQL from "src/types/graphql";
import { IdentityDocumentMatchHint } from "../IdentityDocumentMatchHint";
import {
  IdentityAnalyzeIdMatch,
  Profile,
  ProfileFieldType,
} from "../utils/analyzeIdFormMatchUtils";

type SiblingsPanelProps = {
  siblings: Profile[];
  identityAiProfileMatches?: IdentityAnalyzeIdMatch[];
  onFindInDocument?: (polygons: readonly (readonly Point[])[]) => void;
};

export const SiblingsPanel: React.FC<SiblingsPanelProps> = ({
  siblings,
  identityAiProfileMatches,
  onFindInDocument,
}) => {
  if (siblings.length > 0) {
    return (
      <VStack spacing={4} align="stretch">
        {siblings.map((sibling, index) => {
          const matches = identityAiProfileMatches?.filter(
            (match) => match.id === sibling.id
          );
          const nameMatch = matches?.find(
            (match) => match.profileFieldType === ProfileFieldType.Name
          );
          const dobMatch = matches?.find(
            (match) => match.profileFieldType === ProfileFieldType.DateOfBirth
          );

          return (
            <Box key={sibling.id || index} p={4}>
              <HStack spacing={2} mb={2}>
                <Text fontSize="2xl" fontWeight="bold" color="primary.600">
                  {sibling.first_name || ""} {sibling.last_name || ""}
                </Text>
                <PersonProfileLink
                  personId={sibling?.id}
                  personType={GQL.person_type_enum.applicant}
                  buttonSize="lg"
                />
              </HStack>
              {nameMatch ? (
                <IdentityDocumentMatchHint
                  documentMatchText={nameMatch.documentMatchText}
                  matchHighlights={nameMatch.matchHighlights}
                  onFindInDocument={onFindInDocument}
                />
              ) : null}

              <Box my={3}>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Date of birth
                </Text>
                <Text fontSize="md">
                  {sibling.birth_date
                    ? formatIsoDate(sibling.birth_date, "MM/dd/yyyy")
                    : "N/A"}
                </Text>
                {dobMatch ? (
                  <IdentityDocumentMatchHint
                    documentMatchText={dobMatch.documentMatchText}
                    matchHighlights={dobMatch.matchHighlights}
                    onFindInDocument={onFindInDocument}
                  />
                ) : null}
              </Box>

              {sibling.applicant_attending_schools &&
                sibling.applicant_attending_schools.length > 0 && (
                  <Box my={3}>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      School
                    </Text>
                    <Text fontSize="md">
                      {sibling.applicant_attending_schools[0]?.description ||
                        "N/A"}
                    </Text>
                  </Box>
                )}
            </Box>
          );
        })}
      </VStack>
    );
  } else {
    return (
      <Box p={4} textAlign="center">
        <Text color="gray.600">No siblings information available</Text>
      </Box>
    );
  }
};
